import {
  Component,
  ElementRef,
  HostListener,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
} from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Stop } from '../../../../core/models/stop.model';
import {
  LoadStickyNote,
  STICKY_ACCENT_OPTIONS,
  StickyBoardStyle,
  StickyNoteAccent,
  StickyNoteVisibility,
} from '../../../../core/models/sticky-note.model';
import { StickyNoteService } from '../../../../core/services/sticky-note.service';
import { AuthService } from '../../../../core/services/auth.service';
import { PermissionsService } from '../../../../core/services/permissions.service';
import { ModalService } from '../../../../core/services/modal.service';
import { ToastService } from '../../../../shared/ui/toast/toast.service';
import { ButtonComponent } from '../../../../shared/ui/button/button.component';
import { TextareaComponent } from '../../../../shared/ui/forms/textarea/textarea.component';
import { SelectComponent, SelectOption } from '../../../../shared/ui/forms/select/select.component';
import {
  ButtonGroupComponent,
  ButtonGroupOption,
} from '../../../../shared/ui/forms/button-group/button-group.component';
import { TextComponent } from '../../../../shared/ui/typography/text/text.component';
import { LucideIconComponent } from '../../../../shared/ui/lucide-icon/lucide-icon.component';

type ScopeFilter = 'all' | 'team' | 'personal';

const STICKY_BOARD_STYLE_KEY = 'hv_sticky_board_style';

function readInitialBoardStyle(): StickyBoardStyle {
  try {
    if (typeof localStorage !== 'undefined') {
      const saved = localStorage.getItem(STICKY_BOARD_STYLE_KEY);
      if (saved === 'paper' || saved === 'board') {
        return saved;
      }
    }
  } catch {
    // Ignore storage access errors (SSR / private mode)
  }
  return 'paper';
}

/** Shared across instances on the page so the floating pad and pinned reminder strip stay in sync. */
const sharedBoardStyle = signal<StickyBoardStyle>(readInitialBoardStyle());

@Component({
  selector: 'app-load-sticky-notes',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DatePipe,
    ButtonComponent,
    TextareaComponent,
    SelectComponent,
    ButtonGroupComponent,
    TextComponent,
    LucideIconComponent,
  ],
  templateUrl: './load-sticky-notes.component.html',
  styleUrls: ['./load-sticky-notes.component.css'],
  host: {
    '[class.contents]': 'pinnedStripOnly()',
  },
})
export class LoadStickyNotesComponent {
  readonly loadId = input.required<string>();
  readonly companyId = input.required<string>();
  readonly stops = input<Stop[]>([]);
  readonly notes = input<LoadStickyNote[]>([]);
  readonly isOpen = input<boolean>(false);
  readonly pinnedStripOnly = input<boolean>(false);
  readonly activeStopFilter = input<number | null>(null);
  readonly caretAlign = input<'left' | 'center'>('left');

  readonly openBoard = output<void>();
  readonly closeBoard = output<void>();
  readonly clearStopFilter = output<void>();

  private readonly hostEl = inject(ElementRef<HTMLElement>);
  private readonly stickyService = inject(StickyNoteService);
  private readonly authService = inject(AuthService);
  private readonly perms = inject(PermissionsService);
  private readonly modalService = inject(ModalService);
  private readonly toastService = inject(ToastService);

  readonly accentOptions = STICKY_ACCENT_OPTIONS;
  readonly boardStyle = sharedBoardStyle.asReadonly();
  readonly availableMaxHeightPx = signal<number | null>(null);
  readonly scopeFilter = signal<ScopeFilter>('all');
  readonly composerOpen = signal<boolean>(false);
  readonly hidePinnedStrip = signal<boolean>(false);
  readonly isSaving = signal<boolean>(false);

  // Composer state
  readonly draftContent = signal<string>('');
  readonly draftVisibility = signal<StickyNoteVisibility>('team');
  readonly draftColor = signal<StickyNoteAccent>('amber');
  readonly draftStopAnchor = signal<string>('load');
  readonly draftPinned = signal<boolean>(false);

  // Inline edit state
  readonly editingNoteId = signal<string | null>(null);
  readonly editingContent = signal<string>('');
  readonly editingColor = signal<StickyNoteAccent>('amber');

  readonly visibilityOptions: ButtonGroupOption[] = [
    { value: 'team', label: 'Shared Team', icon: 'Users' },
    { value: 'personal', label: 'Personal', icon: 'Lock' },
  ];

  readonly teamCount = computed(() =>
    this.notes().filter((n) => n.visibility === 'team').length
  );
  readonly personalCount = computed(() =>
    this.notes().filter((n) => n.visibility === 'personal').length
  );
  readonly pinnedNotes = computed(() =>
    this.notes().filter((n) => Boolean(n.pinned))
  );

  readonly scopeFilterOptions = computed<ButtonGroupOption[]>(() => [
    { value: 'all', label: `All (${this.notes().length})` },
    { value: 'team', label: `Shared (${this.teamCount()})`, icon: 'Users' },
    { value: 'personal', label: `Personal (${this.personalCount()})`, icon: 'Lock' },
  ]);

  readonly anchorOptions = computed<SelectOption[]>(() => {
    const opts: SelectOption[] = [{ value: 'load', label: 'Entire Load', icon: 'StickyNote' }];
    for (const s of this.stops() || []) {
      if (s?.stopNumber != null) {
        const loc = [s.address?.city, s.address?.state].filter(Boolean).join(', ');
        opts.push({
          value: String(s.stopNumber),
          label: loc ? `Stop #${s.stopNumber} · ${loc}` : `Stop #${s.stopNumber}`,
          icon: 'MapPin',
        });
      }
    }
    return opts;
  });

  readonly filteredNotes = computed<LoadStickyNote[]>(() => {
    const scope = this.scopeFilter();
    const stopNum = this.activeStopFilter();
    return this.notes().filter((n) => {
      if (scope === 'team' && n.visibility !== 'team') return false;
      if (scope === 'personal' && n.visibility !== 'personal') return false;
      if (stopNum !== null && n.stopNumber !== stopNum) return false;
      return true;
    });
  });

  constructor() {
    // Auto-open composer when user opens an empty sticky board or clicks a specific stop pin with 0 notes
    effect(() => {
      if (this.pinnedStripOnly()) return;
      const open = this.isOpen();
      const stopFilter = this.activeStopFilter();
      if (!open) return;

      queueMicrotask(() => {
        if (typeof requestAnimationFrame === 'function') {
          requestAnimationFrame(() => this.updateAvailableMaxHeight());
        } else {
          this.updateAvailableMaxHeight();
        }
      });

      const currentNotes = untracked(() => this.notes());
      if (stopFilter !== null) {
        this.draftStopAnchor.set(String(stopFilter));
        if (currentNotes.filter((n) => n.stopNumber === stopFilter).length === 0) {
          this.composerOpen.set(true);
        }
      } else if (currentNotes.length === 0) {
        this.composerOpen.set(true);
      }
    });
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    if (this.isOpen() && !this.pinnedStripOnly()) {
      this.updateAvailableMaxHeight();
    }
  }

  updateAvailableMaxHeight(): void {
    if (typeof window === 'undefined' || this.pinnedStripOnly()) return;
    const host = this.hostEl.nativeElement;
    const stage = host.querySelector('.fluent-sticky-stage') as HTMLElement | null;
    const anchor = stage ?? host;
    const rect = anchor.getBoundingClientRect();
    if (rect.top <= 0 && rect.height <= 0) return;

    let bottomBoundary = window.innerHeight;
    let parent = host.parentElement;
    while (parent && parent !== document.body) {
      const style = window.getComputedStyle(parent);
      if (style.overflowY === 'auto' || style.overflowY === 'scroll') {
        const parentRect = parent.getBoundingClientRect();
        if (parentRect.height > 0 && parentRect.bottom > rect.top + 180) {
          bottomBoundary = Math.min(bottomBoundary, parentRect.bottom);
        }
        break;
      }
      parent = parent.parentElement;
    }

    const available = Math.max(
      240,
      Math.min(720, Math.floor(bottomBoundary - Math.max(0, rect.top) - 20))
    );
    this.availableMaxHeightPx.set(available);
  }

  @HostListener('document:keydown.escape', ['$event'])
  onEscapeKey(event: Event): void {
    if (!this.isOpen() || this.pinnedStripOnly()) return;
    // Do not intercept Escape if a modal or CDK dropdown overlay is open
    if (this.modalService.state() !== null) return;
    if (typeof document !== 'undefined' && document.querySelector('.cdk-overlay-pane')) return;

    event.stopPropagation();
    if (this.editingNoteId()) {
      this.cancelEdit();
      return;
    }
    this.closeBoard.emit();
  }

  toggleComposer(): void {
    const next = !this.composerOpen();
    if (next && this.activeStopFilter() !== null) {
      this.draftStopAnchor.set(String(this.activeStopFilter()));
    }
    this.composerOpen.set(next);
  }

  toggleBoardStyle(): void {
    const next: StickyBoardStyle = sharedBoardStyle() === 'paper' ? 'board' : 'paper';
    this.setBoardStyle(next);
  }

  setBoardStyle(style: StickyBoardStyle): void {
    sharedBoardStyle.set(style);
    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem(STICKY_BOARD_STYLE_KEY, style);
      }
    } catch {
      // Ignore storage access errors
    }
  }

  isOwnNote(note: LoadStickyNote): boolean {
    const uid = this.authService.user()?.uid;
    return !!uid && note.authorId === uid;
  }

  canModifyNote(note: LoadStickyNote): boolean {
    if (this.isOwnNote(note)) return true;
    // Admins & Dispatch Managers can curate shared team sticky notes
    return note.visibility === 'team' && this.perms.isDispatchManagerOrAbove();
  }

  formatNoteDate(ts: any): Date | null {
    if (!ts) return null;
    if (typeof ts?.toDate === 'function') return ts.toDate();
    if (typeof ts?.seconds === 'number') return new Date(ts.seconds * 1000);
    if (typeof ts === 'number') return new Date(ts);
    return null;
  }

  async submitDraft(): Promise<void> {
    const text = this.draftContent().trim();
    const companyId = this.companyId();
    const loadId = this.loadId();
    if (!text || !companyId || !loadId || this.isSaving()) return;

    this.isSaving.set(true);
    try {
      const anchor = this.draftStopAnchor();
      const stopNumber = anchor !== 'load' && !Number.isNaN(Number(anchor)) ? Number(anchor) : null;
      await this.stickyService.createNote({
        companyId,
        loadId,
        content: text,
        visibility: this.draftVisibility(),
        color: this.draftColor(),
        stopNumber,
        pinned: this.draftPinned(),
      });
      this.draftContent.set('');
      this.draftPinned.set(false);
      this.composerOpen.set(false);
    } catch (err: any) {
      this.toastService.error(err?.message || 'Could not save sticky note.');
    } finally {
      this.isSaving.set(false);
    }
  }

  async shareWithTeam(note: LoadStickyNote): Promise<void> {
    if (!this.isOwnNote(note)) return;
    try {
      await this.stickyService.updateNote(this.companyId(), note.id, {
        visibility: 'team',
      });
      this.toastService.success('Sticky note shared with the team.');
    } catch (err: any) {
      this.toastService.error(err?.message || 'Failed to share sticky note.');
    }
  }

  async makePersonal(note: LoadStickyNote): Promise<void> {
    if (!this.isOwnNote(note)) return;
    try {
      await this.stickyService.updateNote(this.companyId(), note.id, {
        visibility: 'personal',
      });
      this.toastService.info('Sticky note is now private to you.');
    } catch (err: any) {
      this.toastService.error(err?.message || 'Failed to update note visibility.');
    }
  }

  async togglePin(note: LoadStickyNote): Promise<void> {
    if (!this.canModifyNote(note)) return;
    try {
      const nextPinned = !note.pinned;
      await this.stickyService.updateNote(this.companyId(), note.id, {
        pinned: nextPinned,
      });
      if (nextPinned) {
        this.hidePinnedStrip.set(false);
      }
    } catch (err: any) {
      this.toastService.error(err?.message || 'Failed to update pin.');
    }
  }

  startEdit(note: LoadStickyNote): void {
    this.editingNoteId.set(note.id);
    this.editingContent.set(note.content);
    this.editingColor.set(note.color || 'amber');
  }

  cancelEdit(): void {
    this.editingNoteId.set(null);
    this.editingContent.set('');
  }

  async saveEdit(note: LoadStickyNote): Promise<void> {
    const trimmed = this.editingContent().trim();
    if (!trimmed || this.isSaving()) return;

    this.isSaving.set(true);
    try {
      await this.stickyService.updateNote(this.companyId(), note.id, {
        content: trimmed,
        color: this.editingColor(),
      });
      this.cancelEdit();
    } catch (err: any) {
      this.toastService.error(err?.message || 'Failed to update sticky note.');
    } finally {
      this.isSaving.set(false);
    }
  }

  async removeNote(note: LoadStickyNote): Promise<void> {
    if (!this.canModifyNote(note)) return;
    try {
      await this.stickyService.deleteNote(this.companyId(), note.id);
    } catch (err: any) {
      this.toastService.error(err?.message || 'Failed to delete sticky note.');
    }
  }
}
